import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

// Type declarations for global objects
declare global {
  interface Window {
    TomSelect: any
  }
}

// Turbo type declaration
declare const Turbo: {
  renderStreamMessage(html: string): void
}

/**
 * Filter Manager Stimulus Controller
 * 
 * Manages adding and removing filters/dimensions in dashboard metrics form
 */
export default class extends Controller {
  static targets = [
    "filterSelector",
    "addFilterButton",
    "activeFiltersContainer",
    "dimensionSelector",
    "addDimensionButton",
    "activeDimensionsContainer"
  ]

  static values = {
    dashboardId: Number
  }

  declare readonly filterSelectorTarget: HTMLSelectElement
  declare readonly addFilterButtonTarget: HTMLButtonElement
  declare readonly activeFiltersContainerTarget: HTMLElement
  declare readonly dimensionSelectorTarget: HTMLSelectElement
  declare readonly addDimensionButtonTarget: HTMLButtonElement
  declare readonly activeDimensionsContainerTarget: HTMLElement
  declare readonly dashboardIdValue: number
  connect(): void {
    // Only update button states if the filter/dimension selectors exist
    // This handles the case when the form is loaded without a report class selected
    if (this.hasFilterSelectorTarget || this.hasDimensionSelectorTarget) {
      this.updateButtonStates()
    }
  }

  // Handle selector changes
  filterSelected(): void {
    this.handleSelectorChange('filter')
  }

  dimensionSelected(): void {
    this.handleSelectorChange('dimension')
  }

  // Add items
  addFilter(): void {
    this.addItem('filter')
  }

  addDimension(): void {
    this.addItem('dimension')
  }

  // Remove items
  removeFilter(event: Event): void {
    this.removeItemFromEvent(event, 'filter')
  }

  removeDimension(event: Event): void {
    this.removeItemFromEvent(event, 'dimension')
  }

  // Consolidated private helper methods
  private handleSelectorChange(type: 'filter' | 'dimension'): void {
    const selector = this.getSelectorTarget(type)
    const button = this.getButtonTarget(type)
    button.disabled = !selector.value
  }

  private addItem(type: 'filter' | 'dimension'): void {
    const selector = this.getSelectorTarget(type)
    const selectedItem = selector.value
    if (!selectedItem) return

    if (this.isItemActive(selectedItem, type)) {
      alert(i18n.t('filter_manager.already_added', { type: type.charAt(0).toUpperCase() + type.slice(1) }))
      return
    }

    this.loadFilterFromServer(selectedItem, type)
    this.resetSelector(type)
  }

  private removeItemFromEvent(event: Event, type: 'filter' | 'dimension'): void {
    const button = event.currentTarget as HTMLButtonElement
    const itemName = button.dataset[`${type}Name`]
    if (!itemName) {
      console.error(`No ${type}Name found on button:`, button)
      return
    }

    this.removeItem(itemName, type)
    this.showOptionInSelector(itemName, this.getSelectorTarget(type))
  }

  private isItemActive(itemName: string, type: 'filter' | 'dimension'): boolean {
    const container = this.getContainerTarget(type)
    return !!container.querySelector(`[data-${type}="${itemName}"]`)
  }

  private getSelectorTarget(type: 'filter' | 'dimension'): HTMLSelectElement {
    return type === 'filter' ? this.filterSelectorTarget : this.dimensionSelectorTarget
  }

  private getButtonTarget(type: 'filter' | 'dimension'): HTMLButtonElement {
    return type === 'filter' ? this.addFilterButtonTarget : this.addDimensionButtonTarget
  }

  private getContainerTarget(type: 'filter' | 'dimension'): HTMLElement {
    return type === 'filter' ? this.activeFiltersContainerTarget : this.activeDimensionsContainerTarget
  }

  private resetSelector(type: 'filter' | 'dimension'): void {
    const selector = this.getSelectorTarget(type)
    const button = this.getButtonTarget(type)
    selector.value = ''
    button.disabled = true
  }

  private loadFilterFromServer(itemName: string, type: 'filter' | 'dimension'): void {
    const reportClass = this.getReportClass()
    if (!reportClass) {
      console.error('No report class found')
      return
    }

    const dashboardId = this.getDashboardId()
    if (!dashboardId) {
      console.error('No dashboard ID found')
      return
    }

    const metricId = this.getMetricId()
    
    // Build the URL for the load_filter endpoint
    const url = new URL(`/dashboards/${dashboardId}/metrics/load_filter`, window.location.origin)
    url.searchParams.set('report_class', reportClass)
    url.searchParams.set('filter_name', itemName)
    url.searchParams.set('filter_type', type)
    if (metricId) {
      url.searchParams.set('metric_id', metricId)
    }
    
    // Make a simple fetch request for turbo stream
    fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html'
      }
    })
    .then(response => response.text())
    .then(html => {
      // Apply the turbo stream response directly
      Turbo.renderStreamMessage(html)
      
      // Hide the option from the selector
      const selector = type === 'filter' ? this.filterSelectorTarget : this.dimensionSelectorTarget
      this.hideOptionInSelector(itemName, selector)
    })
    .catch(error => {
      console.error('Error loading filter:', error)
    })
  }

  private removeItem(itemName: string, type: 'filter' | 'dimension'): void {
    const container = this.getContainerTarget(type)
    
    // Try to find by ID first (dynamically added elements)
    let element = document.getElementById(`active_${type}_${itemName}`)
    
    // If not found by ID, search by data attribute (pre-rendered elements)
    if (!element) {
      element = container.querySelector(`[data-${type}="${itemName}"]`)
    }
    
    if (element) {
      element.remove()
    }
  }

  private hideOptionInSelector(itemName: string, selector: HTMLSelectElement): void {
    const option = selector.querySelector(`option[value="${itemName}"]`) as HTMLOptionElement
    if (option) {
      option.style.display = 'none'
    }
  }

  private showOptionInSelector(itemName: string, selector: HTMLSelectElement): void {
    const option = selector.querySelector(`option[value="${itemName}"]`) as HTMLOptionElement
    if (option) {
      option.style.display = 'block'
    }
  }

  private updateButtonStates(): void {
    this.handleSelectorChange('filter')
    this.handleSelectorChange('dimension')
  }

  private getReportClass(): string | null {
    // Look for a report class in the form - this could be from a metric or direct report
    const reportClassInput = document.querySelector('select[name*="report_class"], input[name*="report_class"]') as HTMLInputElement | HTMLSelectElement
    return reportClassInput?.value || null
  }

  private getDashboardId(): string | null {
    // Use the dashboard ID from Stimulus value instead of parsing URL
    return this.dashboardIdValue ? String(this.dashboardIdValue) : null
  }

  private getMetricId(): string | null {
    // Look for metric ID in form fields or URL
    const metricIdInput = document.querySelector('input[name*="[id]"], input[name="id"]') as HTMLInputElement
    if (metricIdInput?.value) {
      return metricIdInput.value
    }
    
    // Try to extract from URL if editing existing metric
    const urlMatch = window.location.pathname.match(/\/metrics\/(\d+)/)
    return urlMatch ? urlMatch[1] : null
  }



}